import React, { useState, useEffect } from 'react';
import axiosInstance from '../AxiosInstance';
import { trackPurchaseEdited } from '../services/analytics';
import {
  Container,
  TextField,
  Button,
  Box,
  Typography,
  MenuItem,
  Card,
  CardContent,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';

const EditPurchase = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [unitTypes, setUnitTypes] = useState([]);
  const [orderPrice, setOrderPrice] = useState('');
  const [quantity, setQuantity] = useState('');
  const [purchaseDate, setPurchaseDate] = useState('');
  const [selectedUnitType, setSelectedUnitType] = useState('');
  const [selectedUnitId, setSelectedUnitId] = useState('');
  const [productDetails, setProductDetails] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [unitSuppliers, setUnitSuppliers] = useState([]);
  const [selectedSupplierId, setSelectedSupplierId] = useState('');
  const [loading, setLoading] = useState(true);
  const [dateWarning, setDateWarning] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [purchaseResponse, productsResponse] = await Promise.all([
          axiosInstance.get(`/purchases/${id}`),
          axiosInstance.get('/products'),
        ]);
        const purchase = purchaseResponse.data;
        setProducts(productsResponse.data);

        const specificProductResponse = await axiosInstance.get(`/products/${purchase.product_id}`);
        const specificProduct = specificProductResponse.data;
        const productNameVariety = `${specificProduct.product_name}${specificProduct.variety ? ` - ${specificProduct.variety}` : ''}`;
        setProductDetails(productNameVariety);
        setOrderPrice(purchase.order_price);
        setQuantity(purchase.quantity);
        setPurchaseDate(purchase.purchase_date.split('T')[0]);

        const unitsResponse = await axiosInstance.get(`/units/product/${purchase.product_id}`);
        const units = unitsResponse.data.map((unit) => ({
          id: unit.unit_id,
          type: unit.unit_category ? `${unit.unit_type} (${unit.unit_category})` : unit.unit_type
        }));
        setUnitTypes(units);

        const expectedUnitType = purchase.unit_category
          ? `${purchase.unit_type} (${purchase.unit_category})`
          : purchase.unit_type;
        const unitExists = units.find(u => u.type === expectedUnitType || u.type.split(' (')[0] === purchase.unit_type);

        if (unitExists) {
          setSelectedUnitType(unitExists.type);
          setSelectedUnitId(unitExists.id);

          const suppliersResponse = await axiosInstance.get(`/units/${unitExists.id}/suppliers`);
          setUnitSuppliers(suppliersResponse.data);
          if (purchase.supplier_id) {
            setSelectedSupplierId(String(purchase.supplier_id));
          }
        } else {
          setSnackbarMessage(`Warning: The unit type "${expectedUnitType}" is no longer available. Please select a new unit type.`);
          setSnackbarSeverity('warning');
          setSnackbarOpen(true);
        }

        setLoading(false);
      } catch (error) {
        setSnackbarMessage('Error fetching data: ' + (error.response?.data?.error || error.message));
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleUnitTypeChange = async (e) => {
    const unitType = e.target.value;
    setSelectedUnitType(unitType);
    setUnitSuppliers([]);
    setSelectedSupplierId('');
    const unit = unitTypes.find(u => u.type === unitType);
    if (unit) {
      setSelectedUnitId(unit.id);
      try {
        const res = await axiosInstance.get(`/units/${unit.id}/suppliers`);
        setUnitSuppliers(res.data);
      } catch (error) {
        console.error('Error fetching unit suppliers:', error);
      }
    }
  };

  const handleProductChange = async (e) => {
    const newProductDetails = e.target.value;
    setProductDetails(newProductDetails);

    // Reset unit and supplier selection when product changes
    setSelectedUnitType('');
    setSelectedUnitId('');
    setUnitTypes([]);
    setUnitSuppliers([]);
    setSelectedSupplierId('');

    if (!newProductDetails) {
      return;
    }

    // Handle cases where variety might not exist
    const productParts = newProductDetails.split(' - ');
    const productName = productParts[0];
    const variety = productParts[1] || null; // Handle undefined variety
    
    const product = products.find(
      (product) => {
        const productMatch = product.product_name === productName;
        const varietyMatch = (!variety && !product.variety) || (variety === product.variety);
        return productMatch && varietyMatch;
      }
    );

    if (product) {
      try {
        console.log('🔧 Fetching units for product:', product.product_id);
        const unitsResponse = await axiosInstance.get(
          `/units/product/${product.product_id}`
        );
        
        if (unitsResponse.data && unitsResponse.data.length > 0) {
          // Filter for buying units only and use consistent format
          const buyingUnits = unitsResponse.data.filter(unit => unit.unit_category === 'buying');
          const units = buyingUnits.map((unit) => ({
            id: unit.unit_id,
            type: unit.unit_category ? `${unit.unit_type} (${unit.unit_category})` : unit.unit_type
          }));
          
          setUnitTypes(units);
          console.log('✅ Units loaded successfully:', units);
          
          if (units.length === 0) {
            setSnackbarMessage('No buying units found for this product. Please add units first.');
            setSnackbarSeverity('warning');
            setSnackbarOpen(true);
          }
        } else {
          setSnackbarMessage('No units found for this product. Please add units first.');
          setSnackbarSeverity('warning');
          setSnackbarOpen(true);
        }
      } catch (error) {
        console.error('Error fetching units:', error);
        setSnackbarMessage('Error fetching units for this product.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } else {
      console.warn('Product not found:', productName, variety);
      setSnackbarMessage('Product not found. Please select a valid product.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!productDetails) {
      setSnackbarMessage('Please select a product.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    if (!selectedUnitType) {
      setSnackbarMessage('Please select a unit type.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    if (unitSuppliers.length > 0 && !selectedSupplierId) {
      setSnackbarMessage('Please select a supplier.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    const chosenSupplier = unitSuppliers.find(s => String(s.supplier_id) === String(selectedSupplierId));

    // Handle cases where variety might not exist
    const productParts = productDetails.split(' - ');
    const productName = productParts[0];
    const variety = productParts[1] || null; // Handle undefined variety

    // Validate that product exists
    if (!productName) {
      setSnackbarMessage('Invalid product selection. Please select a valid product.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    // Map selectedUnitType to unit_id
    const selectedUnit = unitTypes.find(
      (unit) => unit.type === selectedUnitType
    );

    if (!selectedUnit) {
      setSnackbarMessage('Please select a valid unit type.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    try {
      await axiosInstance.put(`/purchases/${id}`, {
        product_name: productName,
        variety: variety,
        supplier_name: chosenSupplier?.name || null,
        supplier_id: chosenSupplier?.supplier_id || null,
        market_name: null,
        order_price: orderPrice,
        quantity: quantity,
        purchase_date: purchaseDate,
        unit_id: selectedUnit.id,
      });
      trackPurchaseEdited(id);
      setSnackbarMessage('Purchase updated successfully!');
      setSnackbarSeverity('success');
      navigate('/dashboard/purchases/view');
    } catch (error) {
      console.error('Error updating purchase:', error);
      setSnackbarMessage('Error updating purchase');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const getOrderPriceLabel = () => {
    return selectedUnitType
      ? `Order Price (per ${selectedUnitType})`
      : 'Order Price per unit';
  };

  const checkFutureDate = (date) => {
    const today = new Date().toISOString().split('T')[0];
    if (date > today) {
      setDateWarning('Warning: You are entering a future date for this purchase.');
    } else {
      setDateWarning('');
    }
  };

  return (
    <Container maxWidth="md">
      <Card>
        <CardContent>
          <Typography variant="h4" gutterBottom>
            Edit Purchase
          </Typography>
          <form onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {!loading && (
                <>
                  <FormControl fullWidth required>
                    <InputLabel>Product Name</InputLabel>
                    <Select
                      value={productDetails}
                      onChange={handleProductChange}
                      disabled={loading}
                      data-testid="edit-purchase-product-select"
                    >
                      {products.map((product) => {
                        const productDisplay = `${product.product_name}${product.variety ? ` - ${product.variety}` : ''}`;
                        return (
                          <MenuItem
                            key={product.product_id}
                            value={productDisplay}
                          >
                            {productDisplay}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth required>
                    <InputLabel>Unit Type</InputLabel>
                    <Select
                      value={selectedUnitType}
                      onChange={handleUnitTypeChange}
                      disabled={loading || unitTypes.length === 0}
                      data-testid="edit-purchase-unit-select"
                    >
                      {unitTypes.map((unit) => (
                        <MenuItem key={unit.id} value={unit.type}>
                          {unit.type}
                        </MenuItem>
                      ))}
                    </Select>
                    {unitTypes.length === 0 && productDetails && (
                      <Typography variant="caption" color="warning.main" sx={{ mt: 1 }}>
                        No units available for this product. Please add units first.
                      </Typography>
                    )}
                  </FormControl>
                  {selectedUnitType && (
                    <FormControl fullWidth required={unitSuppliers.length > 0} disabled={unitSuppliers.length === 0}>
                      <InputLabel>Supplier</InputLabel>
                      <Select
                        value={selectedSupplierId}
                        onChange={(e) => setSelectedSupplierId(e.target.value)}
                        label="Supplier"
                        data-testid="edit-purchase-supplier-select"
                      >
                        {unitSuppliers.length === 0
                          ? <MenuItem value="" disabled>No suppliers available</MenuItem>
                          : unitSuppliers.map(s => (
                              <MenuItem key={s.supplier_id} value={String(s.supplier_id)}>{s.name}</MenuItem>
                            ))
                        }
                      </Select>
                    </FormControl>
                  )}
                  <TextField
                    label={getOrderPriceLabel()}
                    variant="outlined"
                    fullWidth
                    value={orderPrice}
                    onChange={(e) => setOrderPrice(e.target.value)}
                    required
                    type="number"
                    disabled={loading}
                    inputProps={{ 'data-testid': 'edit-purchase-order-price' }}
                  />
                  <TextField
                    label="Quantity"
                    variant="outlined"
                    fullWidth
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    required
                    type="number"
                    disabled={loading}
                    inputProps={{ 'data-testid': 'edit-purchase-quantity' }}
                  />
                  <TextField
                    label="Purchase Date"
                    variant="outlined"
                    fullWidth
                    value={purchaseDate}
                    onChange={(e) => {
                      setPurchaseDate(e.target.value);
                      checkFutureDate(e.target.value);
                    }}
                    required
                    type="date"
                    InputLabelProps={{
                      shrink: true
                    }}
                    disabled={loading}
                    inputProps={{ 'data-testid': 'edit-purchase-date' }}
                  />
                  {dateWarning && (
                    <Typography color="warning.main" variant="body2">
                      {dateWarning}
                    </Typography>
                  )}
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={loading || !productDetails || !selectedUnitType || (unitSuppliers.length > 0 && !selectedSupplierId)}
                    data-testid="edit-purchase-save"
                  >
                    Update Purchase
                  </Button>
                </>
              )}
              {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <Typography>Loading purchase data...</Typography>
                </Box>
              )}
            </Box>
          </form>
        </CardContent>
      </Card>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default EditPurchase;
